<template>
  <main class="medium-12 column login">
    <section class="text-center medium-12 login__hero align-self-top">
      <img
        :src="globalConfig.logo"
        :alt="globalConfig.installationName"
        class="hero__logo"
      />
      <h1 class="hero__title">
        {{
          useInstallationName($t('LOGIN.TITLE'), globalConfig.installationName)
        }}
      </h1>
    </section>
    <section class="row align-center">
      <div v-if="!email" class="small-12 medium-4 column">
        <div class="login-box column align-self-top">
          <GoogleOAuthButton
            v-if="showGoogleOAuth()"
            :callback-url="callbackUrl"
            button-size="large"
            class="oauth-reverse"
          />
          <form class="column log-in-form" @submit.prevent="login()">
            <label :class="{ error: $v.credentials.email.$error }">
              {{ $t('LOGIN.EMAIL.LABEL') }}
              <input
                v-model.trim="credentials.email"
                type="text"
                data-testid="email_input"
                :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
                data-cy="cy-login-email"
                @input="$v.credentials.email.$touch"
              />
            </label>
            <label :class="{ error: $v.credentials.password.$error }">
              {{ $t('LOGIN.PASSWORD.LABEL') }}
              <div class="relative">
                <input
                  v-model.trim="credentials.password"
                  :type="passwordType"
                  data-testid="password_input"
                  :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
                  data-cy="cy-login-password"
                  @input="$v.credentials.password.$touch"
                />
                <img
                  :src="passwordIcon"
                  loading="eager"
                  alt="password icon"
                  class="password-icon"
                  @click="onTogglePasswordType"
                />
              </div>
            </label>
            <woot-submit-button
              :disabled="
                $v.credentials.email.$invalid ||
                  $v.credentials.password.$invalid ||
                  loginApi.showLoading
              "
              :button-text="$t('LOGIN.SUBMIT')"
              :loading="loginApi.showLoading"
              button-class="large expanded"
              cypress-selector="cy-login-submit"
            />
          </form>
        </div>
        <div v-if="!hasCallbackUrl" class="text-center column sigin__footer">
          <p v-if="!globalConfig.disableUserProfileUpdate">
            <router-link to="auth/reset/password">
              {{ $t('LOGIN.FORGOT_PASSWORD') }}
            </router-link>
          </p>
          <p v-if="showSignupLink()">
            <router-link :to="{ path: 'auth/signup', query: $route.query }">
              {{ $t('LOGIN.CREATE_NEW_ACCOUNT') }}
            </router-link>
          </p>
        </div>
      </div>
      <woot-spinner v-else size="" />
    </section>

    <woot-delete-modal
      v-if="showConfirmLoginPopup"
      :show.sync="showConfirmLoginPopup"
      :title="$t('LOGIN.LOGIN_SESSION.TITLE')"
      :message="$t('LOGIN.LOGIN_SESSION.DESC')"
      :confirm-text="$t('LOGIN.LOGIN_SESSION.CONFIRM')"
      :reject-text="$t('LOGIN.LOGIN_SESSION.REJECT')"
      :on-close="closeLogin"
      :on-confirm="confirmLogin"
    />
  </main>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import WootSubmitButton from 'components/buttons/FormSubmitButton';
import { mapGetters } from 'vuex';
import { parseBoolean } from '@chatwoot/utils';
import GoogleOAuthButton from '../../components/ui/Auth/GoogleOAuthButton.vue';
import authAPI from '../../api/auth';
import alertMixin from 'shared/mixins/alertMixin';
import passwordFieldMixin from 'dashboard/mixins/passwordFieldMixin';
import { isValidCallbackUrl } from 'dashboard/helper/URLHelper';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
};

export default {
  components: {
    WootSubmitButton,
    GoogleOAuthButton,
  },
  mixins: [globalConfigMixin, alertMixin, passwordFieldMixin],
  props: {
    ssoAuthToken: { type: String, default: '' },
    impersonate: { type: Boolean, default: false },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    config: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  data() {
    return {
      // We need to initialize the component with any
      // properties that will be used in it
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
      },
      error: '',
      showConfirmLoginPopup: false,
    };
  },
  validations: {
    credentials: {
      password: {
        required,
      },
      email: {
        required,
        email,
      },
    },
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      currentUser: 'getCurrentUser',
    }),
    callbackUrl() {
      const callback = this.$route.query.callback;
      return isValidCallbackUrl(callback) ? callback : null;
    },
    hasCallbackUrl() {
      return this.callbackUrl && this.callbackUrl.length > 0;
    },
  },
  async created() {
    if (this.hasCallbackUrl) {
      const data = await authAPI.getAuthData();
      if (data) {
        const { authorization } = data;
        if (authorization && authorization.length > 0) {
          const { account_id } = this.currentUser;
          const url = new URL(this.callbackUrl);
          const params = new URLSearchParams(url.search);
          // params.append('token', btoa(account_id_signature));
          params.append('account_id', account_id);
          params.append('token', authorization.replace('Bearer ', ''));
          url.search = params.toString();
          window.location = url.toString();
        }
      }
    } else if (this.$route.query.callback) {
      // If callback is provided but invalid, log a warning
      // eslint-disable-next-line no-console
      console.warn(
        'Invalid callback URL provided, proceeding with normal login flow'
      );
    }
    if (this.ssoAuthToken) {
      this.login();
    }
    if (this.authError) {
      const message = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      this.setAlert(this.$t(message), 'error');
      // wait for idle state
      window.requestIdleCallback(() => {
        // Remove the error query param from the url
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    confirmLogin() {
      this.login(false);
    },
    closeLogin() {
      this.showConfirmLoginPopup = false;
      this.loginApi.showLoading = false;
      if (this.email) {
        window.location = '/app/login';
      }
    },
    setAlert(message, type) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      this.showAlert(message, type);
    },
    showSignupLink() {
      return parseBoolean(window.hooryConfig.signupEnabled);
    },
    showGoogleOAuth() {
      return Boolean(window.hooryConfig.googleOAuthClientId);
    },
    login(sessions = true) {
      this.loginApi.showLoading = true;
      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
        callback: this.$route.query.callback,
        impersonate: this.impersonate,
        check_active_sessions: sessions,
      };
      this.$store
        .dispatch('login', credentials)
        .then(() => {
          this.setAlert(this.$t('LOGIN.API.SUCCESS_MESSAGE'), 'success');
        })
        .catch(response => {
          // Reset URL Params if the authentication is invalid
          if (this.email && response && response.status !== 409) {
            window.location = '/app/login';
          }

          if (response && response.status === 401) {
            const { errors } = response.data;
            const hasAuthErrorMsg =
              errors &&
              errors.length &&
              errors[0] &&
              typeof errors[0] === 'string';
            if (hasAuthErrorMsg) {
              this.setAlert(errors[0], 'error');
            } else {
              this.setAlert(this.$t('LOGIN.API.UNAUTH'), 'error');
            }
          } else if (response && response.status === 409) {
            this.showConfirmLoginPopup = true;
          } else {
            this.setAlert(this.$t('LOGIN.API.ERROR_MESSAGE'), 'error');
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'dashboard/assets/scss/password-field-icon.scss';

h1 {
  font-size: 4rem;
}

.oauth-reverse {
  display: flex;
  flex-direction: column-reverse;
}
img.password-icon {
  @extend %password-field;
}
</style>
